import React, { useState, useEffect } from "react";
import "./index.css";
import SearchFilter from "./components/SearchFilter";
import Results from "./components/Results";
import PageContainer from "./PageContainer";
import { YelpAPI, YelpResult } from "./lib/yelp";
import { MoonLoader } from "react-spinners";
import { useToast } from "./context/ToastContext";
import ApiKeyModal from "./components/ApiKeyModal";
import { FaCog } from "react-icons/fa";

function App() {
  const [yelp, setYelp] = useState<YelpAPI | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showToast } = useToast();

  const [yelpResult, setYelpResult] = useState<YelpResult | null>(null);

  useEffect(() => {
    // Try to load API key from cookie
    const savedApiKey = document.cookie
      .split("; ")
      .find((row) => row.startsWith("yelpApiKey="))
      ?.split("=")[1];

    if (savedApiKey) {
      setApiKey(savedApiKey);
      const yelpService = new YelpAPI(savedApiKey);
      setYelp(yelpService);
    }
  }, []);

  const handleApiKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (apiKey.trim()) {
      // Save API key to cookie (expires in 30 days)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);
      document.cookie = `yelpApiKey=${apiKey}; expires=${expiryDate.toUTCString()}; path=/`;

      const yelpService = new YelpAPI(apiKey);
      setYelp(yelpService);
      setIsModalOpen(false);
      showToast("API key saved successfully!", "success");
    } else {
      showToast("Please enter a valid API key", "error");
    }
  };

  return (
    <PageContainer>
      <div className="flex flex-col items-center justify-center max-w-xl mx-auto">
        <div className="w-full flex justify-end mb-4">
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
          >
            <FaCog size={16} />
            <span>{yelp ? "Configure API Key" : "Set API Key"}</span>
          </button>
        </div>

        <ApiKeyModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          apiKey={apiKey}
          onApiKeyChange={setApiKey}
          onSubmit={handleApiKeySubmit}
        />

        {!yelp && (
          <div className="w-full text-center mb-6 p-4 bg-yellow-50 text-yellow-800 rounded-md">
            Please set your Yelp API key to start searching
          </div>
        )}

        <SearchFilter
          yelp={yelp}
          setCurrentItems={setYelpResult}
          setIsLoading={setIsLoading}
        />
        {yelpResult && !isLoading && (
          <Results yelpResults={yelpResult} isLoading={isLoading} />
        )}
        {isLoading && (
          <div className="flex justify-center mt-4 flex-col items-center">
            <MoonLoader size={75} color="#3b82f6" loading={isLoading} />
            <span className="text-gray-500 text-sm mt-4">
              Loading Results...
            </span>
          </div>
        )}
        {!yelpResult && !isLoading && (
          <div className="flex justify-center w-full mt-4">
            <span className="text-gray-500 text-sm">
              Please enter an address or location and click search.
            </span>
          </div>
        )}
        {yelpResult && yelpResult.total === 0 && !isLoading && (
          <div className="flex justify-center w-full mt-4">
            <span className="text-gray-500 text-sm">
              No results for that search.
            </span>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export default App;
